import qs from 'qs'

import HttpClient from 'services/HttpClient'

export interface ILandingPageQuery {
  brandId?: string
  productCategoryId?: string
  country?: string
  search?: string
  limit?: {
    take: number
    page: number
  }
  order?: {
    by: string
    dir: 'asc' | 'desc'
  }
}

export interface ISectionAssetInput {
  landingPageAssetId: string
  orderInSection: number
  selectedVariant: string
  assetGroupId?: string | null
}

export interface ISectionInput {
  landingPageAssetTypeId: string
  /** Config section type UUID; when multiple section types share same primary (e.g. Icons Function), send this so backend knows which type. */
  sectionTypeId?: string
  orderInPage: number
  selectedVariant: string
  assets?: ISectionAssetInput[]
  /** Include this section in the page navigation (max 12 per LP). */
  inNavigation?: boolean
  /** Required when inNavigation === true; used in href="#anchorId". */
  anchorId?: string | null
  /** Optional label for the nav link; if omitted, backend may use anchorId or section type name. */
  navLabel?: string | null
  /** Lead asset (type "Lead") for section title/heading. Lead variant is derived from the section variant. */
  leadLandingPageAssetId?: string | null
  accentColor?: string | null
  backgroundColor?: string | null
  fontColor?: string | null
  secondaryFontColor?: string | null
}

export interface ICreateLandingPageRequest {
  name: string
  brandId: string
  productCategoryIds?: string[] // Required unless templateLanding or templateLandingPageId
  /** Internal note. Not copied when the landing page is duplicated. */
  notes?: string | null
  /** Null keeps the brand default for that role. */
  accentColor?: string | null
  backgroundColor?: string | null
  fontColor?: string | null
  secondaryFontColor?: string | null
  isMultipleProduct: boolean // Flag for single vs multiple product landing pages
  country: string
  landingPageConfigId?: string // Optional config reference
  sections?: ISectionInput[] // Optional: sections with assets can be included in initial request
  /** Active font allowed for brand; omit if none */
  selectedFontId?: string
  templateLanding?: boolean
  templateName?: string | null
  templateDescription?: string | null
  /** Clone sections from this template LP and attach all brand categories */
  templateLandingPageId?: string
}

export interface ILandingPageTemplateSummary {
  id: string
  templateName: string | null
  brandId: string
  country: string
  landingPageConfigId?: string | null
  templateDescription?: string | null
  templatePreviewUrl?: string | null
  templatePreviewMimeType?: string | null
}

export interface IPreviewDraftLandingPageRequest {
  brandId: string
  partnerId: string
  country: string
  landingPageConfigId?: string
  selectedFontId?: string | null
  accentColor?: string | null
  backgroundColor?: string | null
  fontColor?: string | null
  secondaryFontColor?: string | null
  pageBackgroundDesktopUrl?: string | null
  pageBackgroundMobileUrl?: string | null
  sections?: ISectionDto[]
  /** Omit for the whole page. */
  sectionIndex?: number
}

export interface ILandingPagePreviewHtmlResponse {
  html: string
  desktopPreviewWidth?: number
}

export interface ILandingPageTemplateBootstrap {
  template: {
    id: string
    name?: string
    landingPageConfigId?: string
    selectedFontId?: string | null
    selectedFont?: { id: string } | null
    sections?: Array<{
      id?: string
      landingPageAssetTypeId?: string
      landingPageAssetType?: { id: string }
      sectionTypeId?: string | null
      orderInPage: number
      selectedVariant: string
      inNavigation?: boolean
      anchorId?: string | null
      navLabel?: string | null
      leadLandingPageAssetId?: string | null
      leadLandingPageAsset?: { id: string; name?: string } | null
      selectedLeadVariant?: string | null
      sectionAssets?: Array<{
        landingPageAsset?: { id: string; name?: string; templateAsset?: boolean }
        orderInSection: number
        selectedVariant: string
        assetGroupId?: string | null
      }>
    }>
  }
  sections: ISectionDto[]
  landingPageConfigId?: string | null
  selectedFontId?: string | null
  accentColor?: string | null
  backgroundColor?: string | null
  fontColor?: string | null
  secondaryFontColor?: string | null
  pageBackgroundDesktopUrl?: string | null
  pageBackgroundMobileUrl?: string | null
}

/** Per-asset-type occurrence limit for cross sections (e.g. max 4 icons, max 1 photo). */
export interface IAssetTypeLimit {
  landingPageAssetTypeId: string
  minCount?: number
  maxCount?: number
}

export interface ISectionVariantLeadConfig {
  enabled: boolean
  mergedIntoSection?: boolean
  required?: boolean
}

export interface IAssetGroupRule {
  groupKey: string
  anchorAssetTypeId: string
  anchorsMin?: number
  anchorsMax?: number
  memberAssetTypeId: string
  membersMinPerAnchor?: number
  membersMaxPerAnchor?: number
}

export interface ISectionVariantConfig {
  name: string
  label?: string
  allowedLandingPageAssetTypeIds: string[]
  assetTypeLimits?: IAssetTypeLimit[]
  lead?: ISectionVariantLeadConfig
  assetGroups?: IAssetGroupRule[]
}

/** Variant config from variants-with-previews (omits duplicate `name`). */
export type IVariantConfigPayload = Omit<ISectionVariantConfig, 'name'>

/** Config: available section type (create/update). GET config response includes id (UUID). */
export interface IConfigAvailableSectionType {
  /** From GET only: section type UUID; use as URL param for DELETE section type. */
  id?: string
  /** Single: required. Cross: omit (primary = first in allowedLandingPageAssetTypeIds). */
  landingPageAssetTypeId?: string
  /** Single: required. Cross: omit. */
  landingPageAssetTypeName?: string
  /** Cross only: required free-text name (e.g. "Icons & Photos"). */
  sectionTypeName?: string
  /**
   * Cross only: at least one; first = primary.
   * For a text-banner style section that also allows a logo LP asset, include both Banner and Logo
   * type ids here; use `assetTypeLimits` to cap counts per type (e.g. max 1 Logo). Variants stay API-driven.
   */
  allowedLandingPageAssetTypeIds?: string[]
  /** Cross only: per-type occurrence limits (e.g. max 4 icons, max 1 photo). */
  assetTypeLimits?: IAssetTypeLimit[]
  minCount?: number
  maxCount?: number
  defaultCount?: number
  required: boolean
  variants: string[] // e.g. ["photos-grid", "photos-list"]
  variantConfigs?: ISectionVariantConfig[]
}

/** Create config – name is auto-derived from brand. Doc §2.2 */
export interface ICreateConfigRequest {
  brandId: string
  availableSectionTypes: IConfigAvailableSectionType[]
}

/** Edit config – only availableSectionTypes. Doc §2.3 */
export interface IUpdateConfigDto {
  availableSectionTypes?: IConfigAvailableSectionType[]
}

export interface IUpdateLandingPageRequest {
  name: string
  brandId: string
  productCategoryIds: string[]
  notes?: string | null
  isMultipleProduct: boolean
  country: string
  landingPageConfigId?: string
}

/** Update payload: all fields optional; send only what you want to change. PUT /landing-pages/:id */
export interface IUpdateLandingPageDto {
  name?: string
  brandId?: string
  productCategoryIds?: string[]
  notes?: string | null
  accentColor?: string | null
  backgroundColor?: string | null
  fontColor?: string | null
  secondaryFontColor?: string | null
  isMultipleProduct?: boolean
  country?: string
  landingPageConfigId?: string
  sections?: ISectionDto[]
  /** `null` clears font; omit to leave unchanged */
  selectedFontId?: string | null
  templateLanding?: boolean
  templateName?: string | null
  templateDescription?: string | null
}

/** Section in update payload: replaces all sections when present */
export interface ISectionDto {
  landingPageAssetTypeId: string
  /** Config section type UUID; when multiple section types share same primary, send so backend knows which type. */
  sectionTypeId?: string
  orderInPage: number
  selectedVariant: string
  assets?: ISectionAssetDto[]
  inNavigation?: boolean
  anchorId?: string | null
  navLabel?: string | null
  /** Lead asset (type "Lead") for section title/heading. Lead variant is derived from the section variant. */
  leadLandingPageAssetId?: string | null
  accentColor?: string | null
  backgroundColor?: string | null
  fontColor?: string | null
  secondaryFontColor?: string | null
}

export interface ISectionAssetDto {
  landingPageAssetId: string
  orderInSection: number
  selectedVariant: string
  assetGroupId?: string | null
}

export interface IReorderSectionsRequest {
  sectionOrders: {
    sectionId: string
    orderInPage: number
  }[]
}

export interface IAddSectionRequest {
  landingPageAssetTypeId: string
  orderInPage: number
  selectedVariant: string
  landingPageAssetIds?: string[] // Optional: Array of asset IDs to add immediately
  sectionAssets?: ISectionAssetDto[]
  inNavigation?: boolean
  anchorId?: string | null
  navLabel?: string | null
  /** Lead asset (type "Lead") for section title/heading. Lead variant is derived from the section variant. */
  leadLandingPageAssetId?: string | null
}

export interface IAddAssetToSectionRequest {
  landingPageAssetId: string
  orderInSection: number
  selectedVariant: string
  assetGroupId?: string | null
}

/** Update section – all optional. Doc §7.7 / LANDING_PAGES_FRONTEND_IMPLEMENTATION */
export interface IUpdateSectionRequest {
  orderInPage?: number
  selectedVariant?: string
  landingPageAssetIds?: string[] // if provided, replaces all assets in section
  sectionAssets?: ISectionAssetDto[]
  inNavigation?: boolean
  anchorId?: string | null
  navLabel?: string | null
  /** Lead asset (type "Lead"); set to null to clear. Lead variant is derived from the section variant. */
  leadLandingPageAssetId?: string | null
}

/** Doc §3 – Landing page asset type (per brand) */
export interface ILandingPageAssetTypeDto {
  id: string
  name: string
  brand: { id: string; name: string }
  maxNumberPerSection: number
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface ICreateAssetTypeRequest {
  name: string
  brandId: string
  maxNumberPerSection?: number
  subtypes?: string[]
}

export interface IUpdateAssetTypeDto {
  name?: string
  maxNumberPerSection?: number
}

/** Doc §4 – Landing page asset form (per asset type + language) */
export interface ILandingPageAssetFormDto {
  id: string
  landingPageAssetType: { id: string; name: string }
  name: string
  brand: { id: string; name: string }
  lang: string | null
  formFields: IFormFieldDto[]
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface ICreateAssetFormRequest {
  landingPageAssetTypeId: string
  name: string
  brandId: string
  /** Country code for lang-specific form; omit or null for default form (all countries) */
  lang?: string | null
}

/** Doc §5 – Form field (per asset form) */
export interface IFormFieldDto {
  id: string
  fieldName: string
  label: string
  order: number
  required?: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface ICreateFormFieldRequest {
  landingPageAssetFormId: string
  fieldName: string
  label: string
  order: number
  required?: boolean
}

export interface IUpdateFormFieldDto {
  fieldName?: string
  label?: string
  order?: number
  required?: boolean
}

/** Doc §8 – Variant item (internal key + optional label + preview image) */
export interface ILandingPageVariantItem {
  /** Internal variant key — use as selectedVariant in API payloads. */
  name: string
  /** User-facing label from DB; null when unset. */
  label: string | null
  previewImageUrl: string | null
  config?: IVariantConfigPayload
}

/** Doc §8 – Variants list response (read-only). sectionTypeId required when multiple section types share same primary. */
export interface ILandingPageVariantsListResponse {
  assetTypes: {
    /** Section type UUID – use when creating/adding sections so API knows which type (e.g. Icons vs Icons & Photos). */
    sectionTypeId?: string | null
    assetTypeId: string
    assetTypeName: string
    allowedLandingPageAssetTypeIds?: string[]
    assetTypeLimits?: IAssetTypeLimit[]
    variants: ILandingPageVariantItem[]
  }[]
}

export interface IGeneratePackageRequest {
  partnerId: string
  templateName: string
  country: string
  packageType?: 'PACKAGE' | 'IFRAME'
  sections: ISectionContentInput[]
}

export interface ISectionContentInput {
  sectionId: string
  html: string
  css: string
  contentParams?: IContentParam[]
}

export interface IContentParam {
  key: string
  value: string
}

/** Request body for POST /landing-pages/:id/generate-package and generate-iframe */
export interface IGenerateLandingPagePackageRequest {
  partnerId: string
  country: string
  contentParams?: IContentParam[]
}

/** Request body for POST /landing-pages/generate-packages (multiple ZIPs) */
export interface IGenerateLandingPagePackagesRequest {
  landingPageIds: string[]
  partnerId: string
  country: string
  contentParams?: IContentParam[]
}

export class LandingPagesRepository {
  api: typeof HttpClient

  constructor(api: typeof HttpClient) {
    this.api = api
  }

  /**
   * GET /landing-pages – list in API shape (see docs/LIST_FILTERING.md).
   * Request query string: limit[take], limit[page], order[by], order[dir],
   * filter[country], filter[brand][id], filter[productCategory][id], filter[name], search (when name given).
   * Brand/category are sent as UUIDs (filter[brand][id], filter[productCategory][id]), not names.
   * Name filtering requires API support (searchColumns or filter.name in landing-page-list.service).
   */
  getLandingPages(query: ILandingPageQuery) {
    const filter: Record<string, string | Record<string, string>> = {}

    if (query.country) filter.country = query.country
    if (query.brandId) filter.brand = { id: query.brandId }
    if (query.productCategoryId)
      filter.productCategory = { id: query.productCategoryId }
    if (query.search != null && query.search.trim() !== '') {
      filter.name = query.search.trim()
    }

    const params: Record<string, unknown> = {
      limit: query.limit ?? { take: 8, page: 1 },
      order: query.order ?? { by: 'createdAt', dir: 'desc' as const },
      ...(Object.keys(filter).length > 0 && { filter })
    }

    if (query.search != null && query.search !== '') {
      params.search = query.search
    }

    return this.api.get(`/landing-pages?${qs.stringify(params)}`)
  }

  /** GET /landing-pages/all – full list (no pagination). Same filter/order/search shape as getLandingPages. */
  getLandingPagesAll(query?: Omit<ILandingPageQuery, 'limit'>) {
    const filter: Record<string, string | Record<string, string>> = {}

    if (query?.country) filter.country = query.country
    if (query?.brandId) filter.brand = { id: query.brandId }
    if (query?.productCategoryId)
      filter.productCategory = { id: query.productCategoryId }
    if (query?.search != null && query.search.trim() !== '') {
      filter.name = query.search.trim()
    }

    const params: Record<string, unknown> = {
      ...(query?.order && {
        order: { by: query.order.by, dir: query.order.dir }
      }),
      ...(Object.keys(filter).length > 0 && { filter })
    }

    if (query?.search != null && query.search !== '') {
      params.search = query.search
    }

    return this.api.get(`/landing-pages/all?${qs.stringify(params)}`)
  }

  getLandingPageById(id: string) {
    return this.api.get(`/landing-pages/${id}`)
  }

  /** POST /landing-pages/preview-draft – HTML for unsaved editor state. */
  previewDraft(payload: IPreviewDraftLandingPageRequest) {
    return this.api.post<ILandingPagePreviewHtmlResponse>(
      '/landing-pages/preview-draft',
      payload
    )
  }

  /** POST /landing-pages/:id/template-preview – webp, mp4, or webm. Field name: file. */
  uploadLandingPageTemplatePreview(id: string, file: File) {
    const formData = new FormData()

    formData.append('file', file)

    return this.api.post(`/landing-pages/${id}/template-preview`, formData)
  }

  deleteLandingPageTemplatePreview(id: string) {
    return this.api.delete(`/landing-pages/${id}/template-preview`)
  }

  /** POST /landing-pages/:id/page-background/:slot – JPEG, PNG, WebP, or GIF. Field name: file. */
  uploadLandingPagePageBackground(
    id: string,
    slot: 'desktop' | 'mobile',
    file: File
  ) {
    const formData = new FormData()

    formData.append('file', file)

    return this.api.post(`/landing-pages/${id}/page-background/${slot}`, formData)
  }

  deleteLandingPagePageBackground(id: string, slot: 'desktop' | 'mobile') {
    return this.api.delete(`/landing-pages/${id}/page-background/${slot}`)
  }

  listLandingPageTemplates(brandId: string, country: string) {
    return this.api.get('/landing-pages/templates', {
      params: { brandId, country }
    })
  }

  bootstrapLandingPageTemplate(templateId: string) {
    return this.api.get(`/landing-pages/templates/${templateId}/bootstrap`)
  }

  /**
   * Active fonts for LP editor (brand-scoped). Route order: `fonts/for-brand` before `/:id`.
   */
  getLandingPageFontsForBrand(brandId: string) {
    return this.api.get(`/landing-pages/fonts/for-brand/${brandId}`)
  }

  createLandingPage(payload: ICreateLandingPageRequest) {
    return this.api.post('/landing-pages', payload)
  }

  updateLandingPage(id: string, payload: IUpdateLandingPageDto) {
    return this.api.put(`/landing-pages/${id}`, payload)
  }

  copyLandingPage(id: string, payload?: { shouldCopyAssets?: boolean }) {
    return this.api.post(`/landing-pages/${id}/copy`, payload ?? {})
  }

  deleteLandingPage(id: string) {
    return this.api.delete(`/landing-pages/${id}`)
  }

  reorderSections(id: string, payload: IReorderSectionsRequest) {
    return this.api.post(`/landing-pages/${id}/reorder-sections`, payload)
  }

  // Sections
  addSection(landingPageId: string, payload: IAddSectionRequest) {
    return this.api.post(`/landing-pages/${landingPageId}/sections`, payload)
  }

  updateSection(
    landingPageId: string,
    sectionId: string,
    payload: IUpdateSectionRequest
  ) {
    return this.api.put(
      `/landing-pages/${landingPageId}/sections/${sectionId}`,
      payload
    )
  }

  deleteSection(landingPageId: string, sectionId: string) {
    return this.api.delete(
      `/landing-pages/${landingPageId}/sections/${sectionId}`
    )
  }

  // Available Assets
  getAvailableAssets(landingPageId: string, landingPageAssetTypeId: string) {
    return this.api.get(
      `/landing-pages/${landingPageId}/available-assets?landingPageAssetTypeId=${landingPageAssetTypeId}`
    )
  }

  getPlacedAssetUsage(landingPageId: string, assetId: string) {
    return this.api.get(
      `/landing-pages/${landingPageId}/placements/${assetId}/usage`
    )
  }

  // Package Generation (API: partnerId, country, contentParams)
  generatePackage(
    landingPageId: string,
    payload: IGenerateLandingPagePackageRequest
  ) {
    return this.api.post(
      `/landing-pages/${landingPageId}/generate-package`,
      payload
    )
  }

  generateIframe(
    landingPageId: string,
    payload: IGenerateLandingPagePackageRequest
  ) {
    return this.api.post(
      `/landing-pages/${landingPageId}/generate-iframe`,
      payload
    )
  }

  /** POST /landing-pages/generate-packages – generate multiple packages (ZIPs), one per landing page */
  generatePackages(payload: IGenerateLandingPagePackagesRequest) {
    return this.api.post('/landing-pages/generate-packages', payload)
  }

  /** GET /landing-pages/:id/packages – list packages for one landing page (newest first) */
  getPackagesForLandingPage(landingPageId: string) {
    return this.api.get(`/landing-pages/${landingPageId}/packages`)
  }

  // Landing Page Configs
  getConfigByBrandId(brandId: string) {
    return this.api.get(`/landing-page-configs?brandId=${brandId}`)
  }

  createConfig(payload: ICreateConfigRequest) {
    return this.api.post('/landing-page-configs', payload)
  }

  updateConfig(id: string, payload: IUpdateConfigDto) {
    return this.api.put(`/landing-page-configs/${id}`, payload)
  }

  deleteConfig(id: string) {
    return this.api.delete(`/landing-page-configs/${id}`)
  }

  /** Add one section type to config. Response: updated config. */
  addSectionTypeToConfig(configId: string, body: IConfigAvailableSectionType) {
    return this.api.post(
      `/landing-page-configs/${configId}/section-types`,
      body
    )
  }

  /** Remove one section type from config. Use section type id (from GET config) as URL param. Response: updated config. */
  removeSectionTypeFromConfig(configId: string, sectionTypeId: string) {
    return this.api.delete(
      `/landing-page-configs/${configId}/section-types/${sectionTypeId}`
    )
  }

  // Landing Page Asset Types (Doc §3)
  getLandingPageAssetTypes(query?: {
    brandId?: string
    limit?: { take: number; page: number }
  }) {
    const params: Record<string, any> = {}

    if (query?.brandId) params['filter[brandId]'] = query.brandId
    if (query?.limit) {
      params['limit[take]'] = query.limit.take
      params['limit[page]'] = query.limit.page
    }

    const q = Object.keys(params).length ? `?${qs.stringify(params)}` : ''

    return this.api.get(`/landing-page-asset-types${q}`)
  }

  getLandingPageAssetTypesAll() {
    return this.api.get('/landing-page-asset-types/all')
  }

  getLandingPageAssetTypeById(id: string) {
    return this.api.get(`/landing-page-asset-types/${id}`)
  }

  createLandingPageAssetType(payload: ICreateAssetTypeRequest) {
    return this.api.post('/landing-page-asset-types', payload)
  }

  updateLandingPageAssetType(id: string, payload: IUpdateAssetTypeDto) {
    return this.api.patch(`/landing-page-asset-types/${id}`, payload)
  }

  deleteLandingPageAssetType(id: string) {
    return this.api.delete(`/landing-page-asset-types/${id}`)
  }

  getLandingPageAssetSubtypesByTypeId(landingPageAssetTypeId: string) {
    return this.api.get(
      `/landing-page-asset-types/${landingPageAssetTypeId}/subtypes`
    )
  }

  getLandingPageAssetSubtypesAll(landingPageAssetTypeId?: string) {
    const query = landingPageAssetTypeId
      ? `?filter[landingPageAssetType][id]=${landingPageAssetTypeId}`
      : ''

    return this.api.get(`/landing-page-asset-subtypes/all${query}`)
  }

  createLandingPageAssetSubtype(payload: {
    name: string
    landingPageAssetTypeId: string
  }) {
    return this.api.post('/landing-page-asset-subtypes', payload)
  }

  updateLandingPageAssetSubtype(
    id: string,
    payload: { name?: string; orderInLandingPageAssetType?: number }
  ) {
    return this.api.patch(`/landing-page-asset-subtypes/${id}`, payload)
  }

  deleteLandingPageAssetSubtype(id: string) {
    return this.api.delete(`/landing-page-asset-subtypes/${id}`)
  }

  // Landing Page Asset Forms (Doc §4)
  getLandingPageAssetForms(limit?: { take: number; page: number }) {
    const params: Record<string, any> = {}

    if (limit) {
      params['limit[take]'] = limit.take
      params['limit[page]'] = limit.page
    }

    return this.api.get(
      `/landing-page-asset-forms${
        Object.keys(params).length ? `?${qs.stringify(params)}` : ''
      }`
    )
  }

  /** Get form (with formFields) by brand + landing page asset type + lang. Pass lang: 'default' for the default form (lang null). */
  getLandingPageAssetFormsByBrandAndType(params: {
    brandId: string
    landingPageAssetTypeId: string
    lang: string
  }) {
    return this.api.get(
      `/landing-page-asset-forms/by-brand-and-type?${qs.stringify(params)}`
    )
  }

  getLandingPageAssetFormById(id: string) {
    return this.api.get(`/landing-page-asset-forms/${id}`)
  }

  getLandingPageAssetFormsAll() {
    return this.api.get('/landing-page-asset-forms/all')
  }

  createAssetForm(payload: ICreateAssetFormRequest) {
    return this.api.post('/landing-page-asset-forms', payload)
  }

  updateLandingPageAssetForm(
    id: string,
    payload: { name?: string; lang?: string | null }
  ) {
    return this.api.patch(`/landing-page-asset-forms/${id}`, payload)
  }

  deleteLandingPageAssetForm(id: string) {
    return this.api.delete(`/landing-page-asset-forms/${id}`)
  }

  // Landing Page Form Fields (Doc §5)
  getLandingPageFormFieldsByFormId(formId: string) {
    return this.api.get(
      `/landing-page-form-fields/landing-page-asset-form/${formId}`
    )
  }

  createLandingPageFormField(payload: ICreateFormFieldRequest) {
    return this.api.post('/landing-page-form-fields', payload)
  }

  updateLandingPageFormField(id: string, payload: IUpdateFormFieldDto) {
    return this.api.patch(`/landing-page-form-fields/${id}`, payload)
  }

  deleteLandingPageFormField(id: string) {
    return this.api.delete(`/landing-page-form-fields/${id}`)
  }

  // Landing Page Variants – preferred: from config, no partner (variant picker with previews)
  getLandingPageVariantsWithPreviews(brandName: string) {
    return this.api.get(
      `/landing-page-variants/${encodeURIComponent(
        brandName
      )}/variants-with-previews`
    )
  }

  // Alternative: list by scanning filesystem (brand + partner)
  getLandingPageVariantsList(brandName: string, partnerName: string) {
    return this.api.get(
      `/landing-page-variants/${encodeURIComponent(
        brandName
      )}/${encodeURIComponent(partnerName)}/list`
    )
  }

  getLandingPageVariantContent(
    brandName: string,
    partnerName: string,
    contentType: 'html' | 'css',
    assetTypeName: string,
    variantName: string
  ) {
    return this.api.get(
      `/landing-page-variants/${encodeURIComponent(
        brandName
      )}/${encodeURIComponent(partnerName)}/${contentType}/${encodeURIComponent(
        assetTypeName
      )}/${encodeURIComponent(variantName)}`
    )
  }

  /** POST .../preview/:assetTypeName/:variantName – brand-scoped, no partner. multipart/form-data, field: image */
  uploadVariantPreviewImage(
    brandName: string,
    assetTypeName: string,
    variantName: string,
    file: File
  ) {
    const formData = new FormData()

    formData.append('image', file)

    return this.api.post(
      `/landing-page-variants/${encodeURIComponent(
        brandName
      )}/preview/${encodeURIComponent(assetTypeName)}/${encodeURIComponent(
        variantName
      )}`,
      formData
    )
  }

  /** DELETE .../preview/:assetTypeName/:variantName – brand-scoped, no partner */
  deleteVariantPreviewImage(
    brandName: string,
    assetTypeName: string,
    variantName: string
  ) {
    return this.api.delete(
      `/landing-page-variants/${encodeURIComponent(
        brandName
      )}/preview/${encodeURIComponent(assetTypeName)}/${encodeURIComponent(
        variantName
      )}`
    )
  }

  /** PATCH .../label/:assetTypeName/:variantName – set or clear user-facing label */
  updateVariantLabel(
    brandName: string,
    assetTypeName: string,
    variantName: string,
    label: string | null
  ) {
    return this.api.patch(
      `/landing-page-variants/${encodeURIComponent(
        brandName
      )}/label/${encodeURIComponent(assetTypeName)}/${encodeURIComponent(
        variantName
      )}`,
      { label }
    )
  }

  // Available Asset Types (for section type dropdown)
  getAvailableAssetTypes(brandId: string) {
    return this.api.get(
      `/landing-pages/available-asset-types?brandId=${brandId}`
    )
  }

  // Add Asset to Section
  addAssetToSection(
    landingPageId: string,
    sectionId: string,
    payload: IAddAssetToSectionRequest
  ) {
    return this.api.post(
      `/landing-pages/${landingPageId}/sections/${sectionId}/assets`,
      payload
    )
  }

  // Remove Asset from Section
  removeAssetFromSection(
    landingPageId: string,
    sectionId: string,
    assetId: string
  ) {
    return this.api.delete(
      `/landing-pages/${landingPageId}/sections/${sectionId}/assets/${assetId}`
    )
  }

  // Preview Landing Page
  // GET /landing-pages/:id/preview?partnerId=<uuid>&contentParams=...
  // Response: { html: string } – full HTML document from API
  getPreview(
    landingPageId: string,
    partnerId: string,
    contentParams?: Array<{ key: string; value: string }>
  ) {
    const params: Record<string, any> = {
      landingPageId,
      partnerId
    }

    if (contentParams && contentParams.length > 0) {
      params.contentParams = JSON.stringify(contentParams)
    }

    return this.api.get<ILandingPagePreviewHtmlResponse>(
      `/landing-pages/${landingPageId}/preview?${qs.stringify(params)}`,
      { skipAuth: true }
    )
  }

  /**
   * List Landing Page Assets.
   * Two flows:
   * 1) Explicit landing page assets: pass landingPageId (required). API uses that page's brand, country and product categories. Params: landingPageId, country?, limit, order, landingPageAssetTypeId?, isActive?.
   * 2) Browse by brand/category: pass brandId, productCategoryId?, country?, etc. Uses filter[brand][id], filter[productCategory][id], etc.
   */
  listLandingPageAssets(query: {
    landingPageId?: string
    brandId?: string
    productCategoryId?: string
    landingPageAssetTypeId?: string
    landingPageAssetSubtypeId?: string
    country?: string
    isActive?: boolean
    templateAsset?: boolean
    name?: string
    search?: string
    limit?: { take: number; page: number }
    order?: { by?: string; dir?: string }
  }) {
    const limit = query.limit ?? { take: 10, page: 1 }
    const order = query.order ?? { by: 'createdAt', dir: 'desc' as const }
    const subtypeFilter =
      query.landingPageAssetSubtypeId != null
        ? {
            landingPageAssetSubtype: {
              id: query.landingPageAssetSubtypeId
            }
          }
        : {}

    if (query.landingPageId) {
      // Flow 1: explicit assets of a landing page – top-level params only
      const params: Record<string, unknown> = {
        landingPageId: query.landingPageId,
        limit,
        order
      }

      if (query.country != null) params.country = query.country
      if (query.landingPageAssetTypeId != null)
        params.landingPageAssetTypeId = query.landingPageAssetTypeId
      if (query.isActive !== undefined) params.isActive = query.isActive
      const nameValue = (query.name ?? query.search)?.trim()

      if (nameValue) params.name = nameValue
      if (Object.keys(subtypeFilter).length > 0) {
        params.filter = subtypeFilter
      }

      const q = qs.stringify(params)

      return this.api.get(`/landing-page-assets${q ? `?${q}` : ''}`)
    }

    // Flow 2: browse by brand/category – filter-based
    const filter: Record<string, string | boolean | Record<string, string>> = {}

    if (query.brandId != null) filter.brand = { id: query.brandId }
    if (query.country != null) filter.country = query.country
    if (query.productCategoryId != null)
      filter.productCategory = { id: query.productCategoryId }
    if (query.landingPageAssetTypeId != null)
      filter.landingPageAssetType = { id: query.landingPageAssetTypeId }
    if (query.landingPageAssetSubtypeId != null) {
      filter.landingPageAssetSubtype = {
        id: query.landingPageAssetSubtypeId
      }
    }

    const nameValue = (query.name ?? query.search)?.trim()

    if (nameValue) filter.name = nameValue
    if (query.isActive !== undefined) filter.isActive = query.isActive
    if (query.templateAsset !== undefined)
      filter.templateAsset = query.templateAsset

    const params: Record<string, unknown> = {
      limit,
      order,
      ...(Object.keys(filter).length > 0 && { filter })
    }
    const q = qs.stringify(params)

    return this.api.get(`/landing-page-assets${q ? `?${q}` : ''}`)
  }

  // Get Landing Page Asset By ID
  getLandingPageAssetById(id: string) {
    return this.api.get(`/landing-page-assets/${id}`)
  }

  // Create Landing Page Asset (JSON)
  createLandingPageAsset(payload: {
    name: string
    brandId: string
    productCategoryId: string
    landingPageAssetTypeId: string
    country: string
    [key: string]: any // Allow additional fields
  }) {
    return this.api.post('/landing-page-assets', payload)
  }

  // Create Landing Page Asset from Form (with file uploads). Doc §6.4
  createLandingPageAssetFromForm(formData: FormData) {
    return this.api.post('/landing-page-assets/form', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  // Update Landing Page Asset (JSON)
  updateLandingPageAsset(
    id: string,
    payload: {
      name?: string
      [key: string]: any // Allow additional optional fields
    }
  ) {
    return this.api.patch(`/landing-page-assets/${id}`, payload)
  }

  // Update Landing Page Asset from Form (with file uploads). Doc §6.5
  updateLandingPageAssetFromForm(id: string, formData: FormData) {
    return this.api.patch(`/landing-page-assets/${id}/form`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  // Delete Landing Page Asset
  deleteLandingPageAsset(id: string) {
    return this.api.delete(`/landing-page-assets/${id}`)
  }

  // Copy (clone) landing page asset – duplicates LP asset and its files in same LP prefix
  copyLandingPageAsset(id: string) {
    return this.api.post(`/landing-page-assets/${id}/copy`)
  }

  copyAndReplacePlacement(
    landingPageId: string,
    payload: {
      productCategoryId: string
      landingPageAssetId?: string
      sectionAssetId?: string
      sectionId?: string
      slot?: 'lead'
    }
  ) {
    return this.api.post(
      `/landing-pages/${landingPageId}/placements/copy-and-replace`,
      payload
    )
  }

  // Copy from product asset → landing page asset (by product asset UUID)
  copyFromProductAsset(assetId: string) {
    return this.api.post('/landing-page-assets/copy-from-product-asset', {
      assetId
    })
  }

  // Create one LP Hotpoints asset from product hotpoints (by product UUID)
  copyHotpointsFromProduct(productId: string) {
    return this.api.post('/landing-page-assets/copy-hotpoints-from-product', {
      productId
    })
  }
}

 
export default new LandingPagesRepository(HttpClient)
